import { useRef, useEffect, useCallback, useState, Fragment } from 'react';
import cx from 'classnames';
import useSWR from 'swr';
import { api, capitalize } from 'Utils';
import { useDispatch, useSelector } from 'react-redux';
import { useDelayUnmount } from 'Hooks';
import { setSelectedMarker, togglePlaying } from 'Actions';
import { Video, Photo, Volume, Link, Basket } from 'tabler-icons-react';
import {
	SidebarStoryHeader,
	ScrollBar,
	PageMeta,
	SidebarStoryLoading,
	SidebarStoryAccordion,
	SidebarStoryVideo,
	SidebarStoryImage,
	SidebarStorySong,
	SidebarStoryDescription,
	StoryVideos,
	StorySongs,
	StoryImages,
	StoryDescriptions,
	StoryLine
} from 'Elements';
import styles from 'partials/sidebar-story.module.scss';

export const SidebarStory = ({ isOpen, onBack, selectedId }) => {
	const initialState = {
		story: {},
		loading: true,
		currentType: '',
		openTexts: false,
		openVideos: false,
		openImages: false,
		openSounds: false
	};
	const [state, setState] = useState(initialState);
	const osRef = useRef(null);
	const track = useSelector((state) => state.player);
	const dispatch = useDispatch();
	const shouldRenderDesc = useDelayUnmount(state.openTexts, 350);
	const shouldRenderVideos = useDelayUnmount(state.openVideos, 350);
	const shouldRenderImages = useDelayUnmount(state.openImages, 350);
	const shouldRenderSongs = useDelayUnmount(state.openSounds, 350);
	const shouldRenderBox = useDelayUnmount(
		!state.openTexts &&
			!state.openVideos &&
			!state.openImages &&
			!state.openSounds,
		350
	);

	const { data, error } = useSWR(
		selectedId ? api.getStoryDetail(selectedId) : null
	);

	const getData = useCallback(() => {
		setState((prev) => ({
			...prev,
			loading: true
		}));
		if (data) {
			setState((prev) => ({
				...prev,
				story: data.list,
				loading: false
			}));
		}
		if (error) {
			setState((prev) => ({
				...prev,
				loading: false
			}));
		}
	}, [data, error]);

	const handleBack = () => {
		onBack?.();
		dispatch(setSelectedMarker({}));
		track.isPlaying && dispatch(togglePlaying(false));
	};

	const getPersonName = (person) => {
		return `${person.f_name} ${person.l_name}`;
	};

	const getFilesType = (type) => {
		const adminFiles = state.story.files.filter(
			(file) => file.type === type
		);
		const userFiles = state.story.productions.filter(
			(file) => file.type === type
		);
		return [...adminFiles, ...userFiles];
	};

	const getUserText = () => {
		return state.story.productions.filter((item) => item.type === 'text');
	};

	const handleShowMore = (type) => {
		let stateType = `open${capitalize(type)}s`;
		setState((prev) => ({
			...prev,
			[stateType]: true,
			currentType: type
		}));
	};

	const closeDetails = (type) => {
		let stateType = `open${capitalize(type)}s`;
		track.isPlaying && dispatch(togglePlaying(false));
		setState((prev) => ({
			...prev,
			[stateType]: false
		}));
	};

	useEffect(() => {
		selectedId && getData();
	}, [getData, selectedId]);

	const boxClass = cx(styles.box, {
		[styles.open]: isOpen,
		[styles.closing]: !isOpen
	});

	const scrollbarClass = cx({
		[styles.scrollbar]: !state.story.parent || !state.story.children,
		[styles.scrollbar_story]: state.story.parent || state.story.children
	});

	return (
		<Fragment>
			{shouldRenderBox && (
				<div className={boxClass}>
					<PageMeta title={state.story.title} />
					<ScrollBar
						ref={osRef.current}
						className={scrollbarClass}
						options={{
							className: 'os-theme-house',
							overflowBehavior: {
								x: 'hidden'
							},
							scrollbars: {
								autoHide: 'never',
								clickScrolling: true
							}
						}}>
						{state.loading && <SidebarStoryLoading />}
						{!state.loading && (
							<SidebarStoryHeader
								header={state.story}
								onBack={handleBack}
							/>
						)}
						{!state.loading && (
							<div className={styles.content}>
								{state.story.description && (
									<SidebarStoryDescription
										persons={getPersonName(
											state.story.persons[0]
										)}
										text={state.story.description}
										onShowMore={() =>
											handleShowMore('text')
										}
									/>
								)}
								{getFilesType('image').length > 0 && (
									<SidebarStoryAccordion
										title="تصویر"
										icon={Photo}
										isOpenbox={true}>
										<SidebarStoryImage
											hasMore={
												getFilesType('image').length > 1
											}
											image={getFilesType('image').slice(
												0,
												1
											)}
											onShowMore={() =>
												handleShowMore('image')
											}
										/>
									</SidebarStoryAccordion>
								)}
								{getFilesType('video').length > 0 && (
									<SidebarStoryAccordion
										title="ویدیو"
										icon={Video}>
										<SidebarStoryVideo
											hasMore={
												getFilesType('video').length > 1
											}
											video={getFilesType('video')[0]}
											onShowMore={() =>
												handleShowMore('video')
											}
										/>
									</SidebarStoryAccordion>
								)}
								{getFilesType('sound').length > 0 && (
									<SidebarStoryAccordion
										title="صوت"
										icon={Volume}>
										<SidebarStorySong
											hasMore={
												getFilesType('sound').length > 1
											}
											song={getFilesType('sound')[0]}
											onShowMore={() =>
												handleShowMore('sound')
											}
										/>
									</SidebarStoryAccordion>
								)}
								{state.story?.link && (
									<SidebarStoryAccordion
										title="لینک"
										icon={Link}>
										<div>this is link content</div>
									</SidebarStoryAccordion>
								)}
								{state.story?.product && (
									<SidebarStoryAccordion
										title="محصول"
										icon={Basket}>
										<div>this is product content</div>
									</SidebarStoryAccordion>
								)}
							</div>
						)}
					</ScrollBar>
					{(state.story.parent || state.story.children) && (
						<StoryLine story={state.story} />
					)}
				</div>
			)}
			{shouldRenderDesc && (
				<StoryDescriptions
					isOpen={state.openTexts}
					type={state.currentType}
					story={state.story}
					productions={getUserText()}
					onBack={() => closeDetails('text')}
				/>
			)}
			{shouldRenderVideos && (
				<StoryVideos
					isOpen={state.openVideos}
					type={state.currentType}
					story={state.story}
					videos={getFilesType('video')}
					onBack={() => closeDetails('video')}
				/>
			)}
			{shouldRenderImages && (
				<StoryImages
					isOpen={state.openImages}
					type={state.currentType}
					story={state.story}
					images={getFilesType('image')}
					onBack={() => closeDetails('image')}
				/>
			)}
			{shouldRenderSongs && (
				<StorySongs
					isOpen={state.openSounds}
					type={state.currentType}
					story={state.story}
					songs={getFilesType('sound')}
					onBack={() => closeDetails('sound')}
				/>
			)}
		</Fragment>
	);
};
